import type { StatTileTone } from "@/components/company/shared/stat-tile";
import type {
  CompanyTag,
  CompanyWebFigure,
  CompanyWebKeyData,
} from "@shaarp/common";
import {
  BanknoteIcon,
  CalendarIcon,
  MapPinIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";

/** The key figures of a company panel. */
export type KeyFigureId = "revenue" | "headcount" | "founded" | "location";

/** A key-figure tile, whether the base or the web fills it. */
export type KeyFigureTile = {
  id: KeyFigureId;
  icon: LucideIcon;
  tone: StatTileTone;
  labelKey: string;
};

/** The key-figure tiles of a company panel, in their order. */
export const KEY_FIGURE_TILES: readonly KeyFigureTile[] = [
  {
    id: "revenue",
    icon: BanknoteIcon,
    tone: "emerald",
    labelKey: "components.dataTableCompanies.columns.revenue",
  },
  {
    id: "headcount",
    icon: UsersIcon,
    tone: "sky",
    labelKey: "components.dataTableCompanies.columns.employeesCount",
  },
  {
    id: "founded",
    icon: CalendarIcon,
    tone: "amber",
    labelKey: "company.insights.creationDate",
  },
  {
    id: "location",
    icon: MapPinIcon,
    tone: "indigo",
    labelKey: "company.insights.location",
  },
];

export const KEY_FIGURE_IDS: readonly KeyFigureId[] = KEY_FIGURE_TILES.map(
  ({ id }) => id,
);

// One column until the panel is wide enough for two tiles to hold a value.
export const KEY_FIGURES_GRID_CLASS =
  "grid grid-cols-1 gap-3 min-[440px]:grid-cols-2";

/** The web figure of a tile: `undefined` while the model has not written it. */
export type WebFigure = CompanyWebFigure | string | null | undefined;

/** What the chat's tag says of where the company is. */
export type TagPlace = Pick<CompanyTag, "address" | "city">;

/** The town of a French postal address: "Sète" in "32 Grand Rue, 34200 Sète". */
const ADDRESS_TOWN = /\b\d{5}\s+([^,\d]+?)(?:\s+cedex\b[^,]*)?\s*(?:,|$)/giu;

/**
 * The postal code closes a French address: the town follows its last group of
 * five digits, not a box or a building number before it ("BP 10000 Cedex,
 * 69002 Lyon").
 */
const townOfAddress = (
  address: string | null | undefined,
): string | null => {
  const towns = [...(address ?? "").matchAll(ADDRESS_TOWN)];
  return towns[towns.length - 1]?.[1].trim() || null;
};

/**
 * Where the company is: the web's location, else the town of the address the
 * panel shows below — the web's, else the tag's — else the tag's town. Never
 * "Not found" above an address.
 */
const locationOf = (
  keyData: Partial<CompanyWebKeyData>,
  tag: TagPlace | null | undefined,
): WebFigure =>
  keyData.location === undefined
    ? undefined
    : keyData.location ||
      townOfAddress(keyData.address) ||
      townOfAddress(tag?.address) ||
      tag?.city?.trim() ||
      null;

/** What the web profile states for a key figure; `tag` places a company the web did not. */
export function webFigureOf(
  id: KeyFigureId,
  keyData: Partial<CompanyWebKeyData>,
  tag: TagPlace | null | undefined,
): WebFigure {
  switch (id) {
    case "revenue":
      return keyData.revenue;
    case "headcount":
      return keyData.headcount;
    case "founded":
      return keyData.foundedYear;
    case "location":
      return locationOf(keyData, tag);
  }
}

/** "~ value", whether or not the model already wrote the tilde. */
function approximately(value: string): string {
  return `~ ${value.replace(/^~\s*/, "")}`;
}

/** A web figure as it reads: the value when stated, "~ value" when estimated. */
export function webFigureText(figure: CompanyWebFigure): string {
  return figure.estimated ? approximately(figure.value) : figure.value;
}
